/**
 * Referral attribution helpers (Sprint 2 / TASK 7-2).
 *
 * Two-phase flow:
 *
 *   Phase 1 — `captureReferralCodeFromUrl()` runs at app boot. It reads
 *             `?ref=CODE` from the current URL and persists it to
 *             `localStorage.referralCode`. It also strips the param from
 *             the address bar so it doesn't follow the user around.
 *             Safe to call on every load — does nothing when the param
 *             is absent.
 *
 *   Phase 2 — `attributeSignupIfPending()` runs once the new user has
 *             a valid Supabase session (i.e. signup has fully
 *             completed). It calls the `attribute_referral_signup`
 *             SECURITY DEFINER RPC, which inserts a `signup` row into
 *             `referral_events` (idempotent — partial unique index on
 *             `referred_user_id`). On success the local cache is
 *             cleared so a future signup on the same browser doesn't
 *             try to re-attribute the same code.
 *
 * Hard rule (TASK 7-2): never block the auth flow. Both helpers swallow
 * all errors and return false on failure. Attribution is best-effort.
 */

import { supabase } from '../lib/supabase';

const QUERY_KEY    = 'ref';
const STORAGE_KEY  = 'referralCode';

/** Read a string-shaped value out of localStorage in a safe way. */
function readStoredCode(): string | null {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v && v.trim().length > 0 ? v.trim() : null;
  } catch {
    return null;
  }
}

/** Best-effort write — never throws. */
function writeStoredCode(code: string): void {
  try { window.localStorage.setItem(STORAGE_KEY, code); } catch { /* noop */ }
}

/** Best-effort delete — never throws. */
function clearStoredCode(): void {
  try { window.localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
}

/**
 * Phase 1 — capture `?ref=CODE` from the current URL into localStorage.
 *
 * Returns the captured code (or null if no param was present).
 * Safe to call from anywhere; no-op outside a browser context.
 */
export function captureReferralCodeFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get(QUERY_KEY);
    if (!raw) return readStoredCode();

    const code = raw.trim();
    if (!code) return readStoredCode();

    // Outreach links use ?ref=outreach&pid= — not a referral code
    if (code === 'outreach') {
      params.delete(QUERY_KEY);
      const remaining = params.toString();
      const newUrl = window.location.pathname
        + (remaining ? `?${remaining}` : '')
        + window.location.hash;
      try { window.history.replaceState({}, '', newUrl); } catch { /* noop */ }
      return readStoredCode();
    }

    writeStoredCode(code);

    // Strip the `?ref=` param from the URL so it doesn't follow the user
    // through SPA navigation (and doesn't end up in shared tab links).
    params.delete(QUERY_KEY);
    const remaining = params.toString();
    const newUrl = window.location.pathname
      + (remaining ? `?${remaining}` : '')
      + window.location.hash;
    try { window.history.replaceState({}, '', newUrl); } catch { /* noop */ }

    return code;
  } catch {
    return null;
  }
}

/**
 * Maximum age (ms) of an auth.users row to be considered a "new" signup.
 * Google / OAuth accounts that already existed will have a `created_at`
 * older than this — we skip attribution for them so a returning user
 * who follows a referral link does not generate a spurious signup event.
 */
const NEW_USER_WINDOW_MS = 10 * 60 * 1000; // 10 minutes

/**
 * Phase 2 — call `attribute_referral_signup` on Postgres for the
 * currently authenticated user. No-ops when:
 *   • there is no stored code;
 *   • there is no active session;
 *   • the user account was created more than 10 minutes ago (existing user
 *     logging in via a referral link, not a genuine new signup);
 *   • the RPC errors out (RLS, expired code, self-referral, etc).
 *
 * Returns true ONLY when the RPC explicitly returned true (a new
 * `signup` row was inserted). On every other path the stored code is
 * still cleared so we don't keep retrying forever.
 */
export async function attributeSignupIfPending(): Promise<boolean> {
  const code = readStoredCode();
  if (!code) return false;

  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return false;

    // Guard: only attribute genuinely new accounts.
    // OAuth sign-in with an existing account goes through the same
    // "signup" UI path on the client — check the server-side creation
    // timestamp so returning users are never double-attributed.
    const { data: { user } } = await supabase.auth.getUser();
    if (!user?.created_at) {
      clearStoredCode();
      return false;
    }
    const ageMs = Date.now() - new Date(user.created_at).getTime();
    if (ageMs > NEW_USER_WINDOW_MS) {
      // Existing user following a referral link — silently clear so the
      // code does not keep triggering on future sign-ins.
      console.info('[referral] skipping attribution — existing account (age %dms)', ageMs);
      clearStoredCode();
      return false;
    }

    const { data, error } = await supabase.rpc('attribute_referral_signup', {
      p_code: code,
    });

    if (error) {
      // Don't block the signup flow on RPC errors. We still clear so we
      // don't retry every time the user signs in.
      console.warn('[referral] attribute_referral_signup error:', error.message);
      clearStoredCode();
      return false;
    }

    clearStoredCode();
    return data === true;
  } catch (err) {
    console.warn('[referral] attributeSignupIfPending exception:', err);
    clearStoredCode();
    return false;
  }
}
